import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Section, Container } from '../styles/shared';
import { resolveMediaServeUrl } from '../utils/mediaServeUrls';
import { resolvePresentation } from '../utils/blockPresentation';

const Figure = styled.figure`
  margin: 0;
  ${({ $size }) => $size === 'small' && 'max-width: 400px;'}
  ${({ $size }) => $size === 'medium' && 'max-width: 600px;'}
  ${({ $size }) => $size === 'large' && 'max-width: 900px;'}
  ${({ $align, $size }) => $size !== 'full' && $align === 'center' && 'margin-left: auto; margin-right: auto;'}
  ${({ $fullBleed, $size }) => $fullBleed && $size === 'full' && 'max-width: none;'}
  img {
    width: 100%;
    height: ${({ $imgHeight }) => $imgHeight || 'auto'};
    border-radius: ${({ $fullBleed }) => ($fullBleed ? '0' : '8px')};
    ${({ $objectFit }) => $objectFit && `object-fit: ${$objectFit};`}
    ${({ $objectPosition }) => $objectPosition && `object-position: ${$objectPosition};`}
  }
  figcaption { margin-top: 12px; font-size: 0.875rem; color: var(--color-text-light, #666); font-style: italic; }
`;

const isExt = (u) => u?.startsWith('http://') || u?.startsWith('https://');

const ImageBlock = ({ content = {}, settings = {}, tenantSlug, apiBase }) => {
  const { src = '', alt = '', caption = '', link = '', alignment = 'center', size = 'medium' } = content;
  const { backgroundColor = '', padding = 'medium' } = settings;
  // Defaults null → do not emit object-fit/height CSS (today: height:auto, no fit).
  const presentation = resolvePresentation(settings, {
    defaultFit: null,
    defaultPosition: null,
    defaultHeight: null,
  });
  const resolvedSrc = resolveMediaServeUrl(src, tenantSlug, apiBase) || src;
  // If height is set without fit, cover matches prior Gallery/Hero intuition.
  const objectFit = presentation.objectFit
    || (presentation.imageHeight && presentation.imageHeight !== 'auto' ? 'cover' : null);

  const fig = (
    <Figure
      $size={presentation.fullBleed ? 'full' : size}
      $align={alignment}
      $fullBleed={presentation.fullBleed}
      $imgHeight={presentation.imageHeight}
      $objectFit={objectFit}
      $objectPosition={presentation.objectPosition}
    >
      <img src={resolvedSrc} alt={alt} loading="lazy" />
      {caption && <figcaption>{caption}</figcaption>}
    </Figure>
  );

  return (
    <Section
      $padding={padding}
      $bgColor={backgroundColor}
      $fullBleed={presentation.fullBleed}
      style={{ textAlign: alignment }}
    >
      <Container $fullBleed={presentation.fullBleed}>
        {link ? (isExt(link) ? <a href={link} target="_blank" rel="noopener noreferrer">{fig}</a> : <Link to={link}>{fig}</Link>) : fig}
      </Container>
    </Section>
  );
};

export default ImageBlock;
